import { replace, push } from 'connected-react-router';
import { register, login, loginSocial, logout, setApiRequestToken, unsetApiRequestToken } from 'api/auth';
import { getAuthedUserProfile, addPNInstallations } from 'api/users';
import { saveLoginToken, getLoginToken, deleteLoginToken, getUserName } from 'utils/helpers';
import Fingerprint2 from 'fingerprintjs2';
import {
    STORAGEKEY_SHOW_EMAIL_VERIFY, ROUTE_PATHS, USER_TYPES,
    GA_KEY, FBP_KEY, HOTJAR_ID, HOTJAR_SV
} from 'config/constants';
import ReactGA from 'react-ga';
import ReactPixel from 'react-facebook-pixel';
import Hotjar from 'utils/hotjar';

const APP_IS_READY = 'APP_IS_READY';
const START_AUTH = 'START_AUTH';
const STOP_AUTH = 'STOP_AUTH';
const FAIL_AUTH = 'FAIL_AUTH';
const AUTH_LOGOUT_SUCCESS = 'AUTH_LOGOUT_SUCCESS';
const AUTHENTICATE = 'AUTHENTICATE';
const UPDATE_PROFILE_DATA = 'UPDATE_PROFILE_DATA';
const UPDATE_READ_NOTIFICATIONS = 'UPDATE_READ_NOTIFICATIONS';

function appIsReady() {
    return {
        type: APP_IS_READY
    };
}

function startAuth() {
    return {
        type: START_AUTH
    };
}

function stopAuth() {
    return {
        type: STOP_AUTH
    };
}

function failAuth(data) {
    return {
        type: FAIL_AUTH,
        data
    };
}

function authLogoutSuccess() {
    return {
        type: AUTH_LOGOUT_SUCCESS
    };
}

export function authenticate(data) {
    return {
        type: AUTHENTICATE,
        data
    };
}

export function updateProfileData(data) {
    return {
        type: UPDATE_PROFILE_DATA,
        data
    };
}

export function updateReadNotifications(count) {
    return {
        type: UPDATE_READ_NOTIFICATIONS,
        count
    };
}

function handlePostInitialLogin(dispatch, profileData, redirectRoute = ROUTE_PATHS.NEWSFEED) {
    const isOnboarded = !!profileData.postal_code_id;
    const isEmailConfirmed = !!profileData.confirmed_at;
    const isRep = profileData.role_type === USER_TYPES.DEPUTE ||
        profileData.role_type === USER_TYPES.SENATEUR ||
        profileData.role_type === USER_TYPES.CANDIDATE;
    const isOrg = profileData.is_organization;

    if (!isRep && (!isOnboarded || !isEmailConfirmed)) {
        dispatch(replace(ROUTE_PATHS.ONBOARDING_AFTER, {
            isOnboarded,
            isEmailConfirmed,
            redirectRoute
        }));

        sessionStorage.setItem(STORAGEKEY_SHOW_EMAIL_VERIFY, 1);
    }
    else {
        if (isOrg) {
            dispatch(replace(ROUTE_PATHS.DASHBOARD_ORG_SUPPORT));
        }
        else if (isRep) {
            dispatch(replace(ROUTE_PATHS.DASHBOARD_REP_CAMPAIGNS));
        }
        else {
            dispatch(replace(redirectRoute));
        }
    }
}

export function handleLoginViaEmail(email, password, remember, redirectRoute) {
    return async (dispatch) => {
        dispatch(startAuth());

        try {
            const { data: { token } } = await login(email, password);

            saveLoginToken(token, remember);
            setApiRequestToken(token);

            const { data } = await getAuthedUserProfile();

            dispatch(authenticate(data));
            handlePostInitialLogin(dispatch, data, redirectRoute);
        }
        catch (err) {
            let message = 'Une erreur s\'est produite lors de la connexion.';

            if (err.response && err.response.status !== 500) {
                message = 'Email ou mot de passe incorrect';
            }

            dispatch(failAuth({ type: 'login', message }));
        }
    };
}

export function handleSocialLogin(provider, providerToken, redirectRoute) {
    return async (dispatch) => {
        dispatch(startAuth());

        try {
            const { data: { token } } = await loginSocial(provider, providerToken);

            saveLoginToken(token, true);
            setApiRequestToken(token);

            const { data } = await getAuthedUserProfile();

            dispatch(authenticate(data));
            handlePostInitialLogin(dispatch, data, redirectRoute);
        }
        catch (err) {
            let message = 'Une erreur s\'est produite lors de la connexion.';

            if (err.response && err.response.status === 401) {
                // No account yet
                dispatch(push(ROUTE_PATHS.ONBOARDING));
                dispatch(stopAuth());

                return;
            }

            dispatch(failAuth({ type: 'login', message }));
        }
    };
}

export function handleRegistration(params, socialProviderParams = null) {
    return async (dispatch) => {
        dispatch(startAuth());

        try {
            const { data: { token } } = await register(params);

            if (socialProviderParams) {
                await loginSocial(
                    socialProviderParams.provider,
                    socialProviderParams.token
                );
            }

            // rep's do not login on registration success
            // instead stop here & trigger the callback if given
            if (params.role_type !== 1) {
                if (params.callback) {
                    params.callback();
                }

                return;
            }

            saveLoginToken(token, true);
            setApiRequestToken(token);

            const { data } = await getAuthedUserProfile();

            dispatch(authenticate(data));
            handlePostInitialLogin(dispatch, data, params.redirectRoute || ROUTE_PATHS.NEWSFEED);
        }
        catch (err) {
            let message = 'Une erreur s\'est produite lors de l\'enregistrement';

            if (err.response) {
                if (err.response.status === 422) {
                    message = 'Cette adresse email est déjà associée à un compte existant';
                }
                else if (err.response.status === 400) {
                    message = 'Cette adresse ne semble pas figurer dans la liste officielle, merci d\'utiliser votre adresse Assemblée nationale ou Sénat. S\'il s\'agit d\'une erreur, merci de nous écrire à contact@factio.org et nous vous répondrons immédiatement !';
                }
            }

            dispatch(failAuth({ type: 'register', message }));
        }
    };
}

export function handleAutoLogin(location) {
    return async (dispatch) => {
        try {
            const token = getLoginToken();

            if (token !== null) {
                setApiRequestToken(token);

                const { data } = await getAuthedUserProfile();
                const isOnboarded = !!data.postal_code_id;
                const isEmailConfirmed = !!data.confirmed_at;
                const defaultRoute = data.is_organization ? ROUTE_PATHS.DASHBOARD_ORG_SUPPORT : ROUTE_PATHS.NEWSFEED;

                dispatch(authenticate(data));

                if (!isOnboarded || (!isEmailConfirmed && location.pathname.indexOf(ROUTE_PATHS.CONFIRM_EMAIL) === -1)) {
                    if (isOnboarded) {
                        let emailVerifySessionCount = sessionStorage.getItem(STORAGEKEY_SHOW_EMAIL_VERIFY);

                        if (!emailVerifySessionCount) {
                            sessionStorage.setItem(STORAGEKEY_SHOW_EMAIL_VERIFY, 1);
                        }
                        else {
                            emailVerifySessionCount = JSON.parse(emailVerifySessionCount);
                            emailVerifySessionCount++;
                            sessionStorage.setItem(STORAGEKEY_SHOW_EMAIL_VERIFY, emailVerifySessionCount);

                            if (emailVerifySessionCount % 5 !== 0) {
                                if (location.pathname === ROUTE_PATHS.LANDING) {
                                    dispatch(replace(defaultRoute));
                                }

                                return;
                            }
                        }
                    }
                    else {
                        sessionStorage.setItem(STORAGEKEY_SHOW_EMAIL_VERIFY, 1);
                    }

                    dispatch(replace(ROUTE_PATHS.ONBOARDING_AFTER, {
                        isOnboarded,
                        isEmailConfirmed,
                        redirectRoute: location.pathname === ROUTE_PATHS.LANDING || location.pathname === ROUTE_PATHS.ONBOARDING_AFTER
                            ? defaultRoute
                            : location.pathname
                    }));

                    return;
                }

                if (location.pathname === ROUTE_PATHS.LANDING) {
                    dispatch(replace(defaultRoute));
                }
            }
        }
        catch (err) {
            // console.warn(err);

            dispatch(handleLogout(true));
        }
        finally {
            dispatch(appIsReady());
        }
    };
}

export function handleLogout(isAutoLogout = false) {
    return async (dispatch) => {
        deleteLoginToken();

        try {
            await logout();
        }
        finally {
            unsetApiRequestToken();

            if (isAutoLogout === true) {
                dispatch(authLogoutSuccess());
            }
            else {
                window.location = window.location.origin;
            }
        }
    };
}

function setAnalyticsProfileData(profileData) {
    ReactPixel.fbq('setUserProperties', FBP_KEY, {
        $account_created_time: profileData.created_at,
        $user_type: profileData.role_type,
        $zipcode: profileData.postal_code
            ? profileData.postal_code.code
            : null,
        facebook_id: profileData.facebook_id
    });
    ReactGA.set({
        userId: profileData.id,
        userName: getUserName(profileData),
        userType: profileData.role_type
    });
}

function setPushNotificationInstallations() {
    const getFingerPrint = (obj) => {
        const mapValue = (item) => item.value;
        const values = obj.map(mapValue);
        const uniqHash = Fingerprint2.x64hash128(values.join(''), 31);

        addPNInstallations({ installation_id: uniqHash });
    };

    const requestFingerPrint = () => Fingerprint2.get(getFingerPrint);

    if (window.requestIdleCallback) {
        requestIdleCallback(requestFingerPrint);
    }
    else {
        setTimeout(requestFingerPrint, 500);
    }
}

const initialState = {
    isAppReady: false,
    isAuthenticating: false,
    isAuthed: false,
    authError: {},
    profileData: {},
    unreadNotifications: 0,
    isPaying: false,
    isAdmin: false,
    isRep: false,
    isDepute: false,
    isSenateur: false
};

export default function authentication(state = initialState, action) {
    switch (action.type) {
        case APP_IS_READY: {
            const { isAuthed, profileData } = state;
            const { id } = profileData;

            Hotjar.initialize(HOTJAR_ID, HOTJAR_SV);
            ReactGA.initialize(GA_KEY);

            if (isAuthed) {
                ReactPixel.init(FBP_KEY, { uid: id });

                setAnalyticsProfileData(profileData);
                setPushNotificationInstallations();
            }
            else {
                ReactPixel.init(FBP_KEY);
            }

            ReactGA.set({ page: window.location.pathname });
            ReactGA.pageview(window.location.pathname);
            ReactPixel.pageView();

            return {
                ...state,
                isAppReady: true
            };
        }

        case START_AUTH:
            return {
                ...state,
                isAuthenticating: true
            };

        case STOP_AUTH:
            return {
                ...state,
                isAuthenticating: false
            };

        case FAIL_AUTH:
            return {
                ...state,
                isAuthenticating: false,
                authError: action.data
            };

        case AUTHENTICATE: {
            const isDepute = action.data.role_type === USER_TYPES.DEPUTE;
            const isSenateur = action.data.role_type === USER_TYPES.SENATEUR;
            const isCandidate = action.data.role_type === USER_TYPES.CANDIDATE;
            const isRep = isDepute || isSenateur || isCandidate;

            if (state.isAppReady) {
                setAnalyticsProfileData(action.data);
            }

            return {
                ...state,
                isAuthenticating: false,
                isAuthed: true,
                authError: {},
                profileData: action.data,
                unreadNotifications: action.data.unread_notifications_count,
                isAdmin: action.data.role_type === USER_TYPES.ADMIN,
                isPaying: !!action.data.paying,
                isRep,
                isDepute,
                isSenateur,
                isCandidate
            };
        }

        case UPDATE_PROFILE_DATA:
            return {
                ...state,
                profileData: {
                    ...state.profileData,
                    ...action.data
                }
            };

        case UPDATE_READ_NOTIFICATIONS: {
            const unreadNotifications = state.unreadNotifications - action.count;

            return {
                ...state,
                unreadNotifications: unreadNotifications < 0
                    ? 0
                    : unreadNotifications
            };
        }

        case AUTH_LOGOUT_SUCCESS:
            return initialState;

        default:
            return state;
    }
}
